import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { FieldHint } from '../components/ui/Field';
import { Input, InputGroup, InputAddon } from '../components/ui/Input';
import { Callout } from '../components/ui/Callout';
import { Icon } from '../components/ui/Icon';
import { AuthShell } from '../components/layout/AuthShell';

interface LocationState {
  from?: {
    pathname: string;
  };
  message?: string;
}

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required')
});

type LoginFormData = z.infer<typeof loginSchema>;

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user, isAuthenticated } = useAuth();
  const { translate } = useLanguage();
  const [isSubmit, setIsSubmit] = useState(false);

  const state = location.state as LocationState;
  const from = state?.from?.pathname || '/';
  const successMessage = state?.message;

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success] = useState(successMessage || '');
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit: hookFormSubmit,
    formState: { errors }
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' }
  });

  useEffect(() => {
    if (isAuthenticated && user && !isSubmit) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, user, navigate, from, isSubmit]);

  const onSubmit = async (data: LoginFormData) => {
    setError('');
    setIsLoading(true);
    setIsSubmit(true);

    try {
      await login({ email: data.email, password: data.password });

      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const u = JSON.parse(storedUser);
        if (!u.verified) {
          navigate('/verify-email', { replace: true });
          return;
        }
      }

      navigate(from, { replace: true });
    } catch (err) {
      const typedError = err as AxiosError<any>;
      if (!typedError.response && err instanceof Error) {
        setError(err.message);
        return;
      }

      const errorMessages = typedError.response?.data?.errors;
      const apiMessage = typedError.response?.data?.message;

      if (errorMessages && (errorMessages as any).email) {
        setError((errorMessages as any).email[0]);
      } else if (apiMessage) {
        setError(apiMessage);
      } else {
        setError(translate('login-failed'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthShell
      title={translate('welcome-back')}
      sub={translate('sign-in-to-continue')}
      footer={
        <>
          {translate('dont-have-account')}{' '}
          <RouterLink to="/signup" style={{ color: 'var(--brand-blue)', fontWeight: 600, textDecoration: 'none' }}>
            {translate('sign-up')}
          </RouterLink>
        </>
      }
    >
      {error && (
        <div style={{ marginBottom: 16 }}>
          <Callout tone="warn">{error}</Callout>
        </div>
      )}

      {success && (
        <div style={{ marginBottom: 16 }}>
          <Callout tone="ok">{success}</Callout>
        </div>
      )}

      <form onSubmit={hookFormSubmit(onSubmit)} noValidate>
        <div className="field">
          <label className="field-label" htmlFor="email">{translate('email-address')}</label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            autoFocus
            className="input-lg"
            {...register('email')}
          />
          {errors.email && (
            <FieldHint role="alert" style={{ color: 'var(--err)' }}>
              {errors.email.message}
            </FieldHint>
          )}
        </div>

        <div className="field" style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label className="field-label" htmlFor="password">{translate('password')}</label>
            <RouterLink
              to="/reset-password"
              style={{ fontSize: 12, color: 'var(--ink-3)', textDecoration: 'none' }}
            >
              {translate('forgot-password')}
            </RouterLink>
          </div>
          <InputGroup>
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              className="input-lg"
              {...register('password')}
            />
            <InputAddon>
              <button
                type="button"
                style={{
                  background: 'transparent',
                  border: 0,
                  padding: '0 4px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  color: 'var(--ink-3)',
                }}
                onClick={() => setShowPassword(v => !v)}
                aria-label={translate(showPassword ? 'hide-password' : 'show-password')}
              >
                <Icon name="eye" size={16} />
              </button>
            </InputAddon>
          </InputGroup>
          {errors.password && (
            <FieldHint role="alert" style={{ color: 'var(--err)' }}>
              {errors.password.message}
            </FieldHint>
          )}
        </div>

        <button
          type="submit"
          className="btn btn-primary btn-block btn-lg"
          style={{ marginTop: 18 }}
          disabled={isLoading}
        >
          {isLoading ? 'Signing in…' : translate('sign-in')}
          {!isLoading && <Icon name="arrowRight" size={15} />}
        </button>
      </form>
    </AuthShell>
  );
};

export default LoginPage;
